import { Option } from 'types/general.types';
import { ReactComponent as ChevronIcon } from 'resources/images/chevron-down.svg';
import { ReactComponent as MagicSolid } from 'resources/images/magic-solid.svg';
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Text,
  TooltipProps,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getCoWriterBarSettings } from 'app/store/Settings/selectors';
import { setCoWriterBarSettings } from 'app/store/Settings/slice';
import { Editor } from '@tiptap/react';
import { getUserGuid } from 'app/utils/helpers';
import { isCoWriterLimitExceeded } from 'app/store/Subscription/selectors';
import { setShowUpgradePopup } from 'app/store/Storage/slice';

interface CoWriterSelectProps {
  variant?: 'icon' | 'text';
  tooltip?: string;
  tooltipPlacement?: TooltipProps['placement'];
  editor?: Editor;
}

export const CoWriterSelect = ({
  variant = 'icon',
  tooltip,
  editor,
}: CoWriterSelectProps) => {
  const dispatch = useAppDispatch();

  const coWriterLimitExceeded = useAppSelector(isCoWriterLimitExceeded);

  const coWriterBar = useAppSelector(getCoWriterBarSettings);

  const LOCAL_STORAGE_KEY = `co-writer-new-${getUserGuid()}`;
  const isNew = localStorage.getItem(LOCAL_STORAGE_KEY) !== 'false';
  const options: Option[] = [
    {
      title: 'Rhymes',
      value: 'rhymes',
    },
    {
      title: 'Synonyms',
      value: 'synonyms',
    },
    {
      title: 'Alliteration',
      value: 'alliteration',
    },
    {
      title: 'Word Chains',
      value: 'word-chain',
    },
  ];

  const onChange = (value: Option['value']) => {
    dispatch(
      setCoWriterBarSettings({
        type: value as 'rhymes' | 'synonyms' | 'alliteration' | 'word-chain',
        lyrics: editor?.getText() || coWriterBar?.lyrics,
      })
    );
  };

  const selectedOption = options.find(
    (option) => option.value === coWriterBar?.type
  );
  return (
    <Flex gap={2}>
      <Menu>
        <MenuButton
          as={Button}
          variant="unstyled"
          title={tooltip}
          aria-haspopup="true"
          data-testid="ai-feature"
          color="textSubdued"
          onClick={(e) => {
            if (coWriterLimitExceeded) {
              e.preventDefault();
              dispatch(
                setShowUpgradePopup({
                  messageType: 'cowriter',
                  context: { action: 'openCowriter' },
                })
              );
            }
          }}
          _hover={{
            svg: {
              color: 'M.70',
            },
          }}
        >
          <Flex alignItems="center" gap={1}>
            {variant === 'text' ? (
              <>
                <Text color="inherit" fontWeight={500}>
                  {selectedOption?.title}
                </Text>
                <Icon
                  as={ChevronIcon}
                  width={3}
                  height={3}
                  fill="currentColor"
                />
              </>
            ) : (
              <Icon as={MagicSolid} height={4} width={4} color="iconBase" />
            )}
          </Flex>
        </MenuButton>

        <MenuList>
          {options.map((option) => (
            <MenuItem
              onClick={() => {
                localStorage.setItem(LOCAL_STORAGE_KEY, 'false');
                onChange(option.value);
              }}
              key={option.value}
              value={option.value}
              data-testid="ai-feature-option"
            >
              {option.title}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {variant === 'icon' && isNew && (
        <Tag variant="solid" size="sm">
          New
        </Tag>
      )}
    </Flex>
  );
};
